import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Truck, LogOut, Plus, Trash2, Save, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ZONE_ICONS = {
  peninsula: { emoji: 'ES', color: 'border-black' },
  baleares: { emoji: 'IB', color: 'border-blue-400' },
  canarias: { emoji: 'IC', color: 'border-yellow-400' },
  europa: { emoji: 'EU', color: 'border-blue-600' },
  usa: { emoji: 'US', color: 'border-red-400' },
  resto_mundo: { emoji: 'WW', color: 'border-green-400' }
};

export default function AdminShipping({ user, setUser }) {
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    if (!user || user.role !== 'admin') { navigate('/admin/login'); return; }
    fetchRates();
  }, [user, navigate]);

  const fetchRates = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/admin/shipping-rates`, { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await res.json();
      if (data.rates && data.rates.length > 0) {
        setRates(data.rates);
      } else {
        await seedDefaults();
      }
    } catch (e) { toast.error('Error al cargar tarifas'); }
    finally { setLoading(false); }
  };

  const seedDefaults = async () => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${BACKEND_URL}/api/admin/shipping-rates/seed`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } });
      const res = await fetch(`${BACKEND_URL}/api/admin/shipping-rates`, { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await res.json();
      setRates(data.rates || []);
      toast.success('Tarifas por defecto creadas');
    } catch (e) { toast.error('Error al crear tarifas por defecto'); }
  };

  const saveZone = async (zone) => {
    const rate = rates.find(r => r.zone === zone);
    if (!rate) return;
    setSaving(prev => ({ ...prev, [zone]: true }));
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/admin/shipping-rates/${zone}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(rate)
      });
      if (res.ok) toast.success(`Tarifas de "${rate.zone_label}" guardadas`);
      else toast.error('Error al guardar');
    } catch (e) { toast.error('Error de conexion'); }
    finally { setSaving(prev => ({ ...prev, [zone]: false })); }
  };

  const updateTier = (zone, tierIdx, field, value) => {
    setRates(prev => prev.map(r => {
      if (r.zone !== zone) return r;
      const tiers = [...r.tiers];
      tiers[tierIdx] = { ...tiers[tierIdx], [field]: field === 'label' ? value : parseFloat(value) || 0 };
      return { ...r, tiers };
    }));
  };

  const addTier = (zone) => {
    setRates(prev => prev.map(r => {
      if (r.zone !== zone) return r;
      return { ...r, tiers: [...r.tiers, { min_subtotal: 0, shipping_cost: 0, label: '' }] };
    }));
  };

  const removeTier = (zone, tierIdx) => {
    setRates(prev => prev.map(r => {
      if (r.zone !== zone) return r;
      return { ...r, tiers: r.tiers.filter((_, i) => i !== tierIdx) };
    }));
  };

  const toggleActive = (zone) => {
    setRates(prev => prev.map(r => r.zone === zone ? { ...r, is_active: !r.is_active } : r));
  };

  const handleLogout = () => { localStorage.removeItem('token'); setUser(null); navigate('/'); };

  if (loading) return <div className="min-h-screen bg-white flex items-center justify-center">Cargando...</div>;

  const zoneOrder = ['peninsula', 'baleares', 'canarias', 'europa', 'usa', 'resto_mundo'];
  const sortedRates = [...rates].sort((a, b) => zoneOrder.indexOf(a.zone) - zoneOrder.indexOf(b.zone));

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-shipping-page">
      <header className="bg-black text-white">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Playfair Display' }}>ADMIN - ARTHUR FITZ</h1>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-white hover:underline">Ver Tienda</Link>
            <Button onClick={handleLogout} variant="outline" className="text-white border-white"><LogOut className="w-4 h-4 mr-2" /> Cerrar Sesion</Button>
          </div>
        </div>
      </header>

      <nav className="bg-white border-b">
        <div className="container mx-auto px-6 py-3">
          <div className="flex gap-6 overflow-x-auto">
            <Link to="/admin" className="text-gray-600 hover:text-black whitespace-nowrap">Dashboard</Link>
            <Link to="/admin/products" className="text-gray-600 hover:text-black whitespace-nowrap">Productos</Link>
            <Link to="/admin/orders" className="text-gray-600 hover:text-black whitespace-nowrap">Pedidos</Link>
            <Link to="/admin/customers" className="text-gray-600 hover:text-black whitespace-nowrap">Clientes</Link>
            <Link to="/admin/coupons" className="text-gray-600 hover:text-black whitespace-nowrap">Cupones</Link>
            <Link to="/admin/quotes" className="text-gray-600 hover:text-black whitespace-nowrap">Presupuestos</Link>
            <Link to="/admin/shipping" className="font-medium text-black border-b-2 border-black pb-2 whitespace-nowrap">Envios</Link>
            <Link to="/admin/contact-requests" className="text-gray-600 hover:text-black whitespace-nowrap">Solicitudes</Link>
            <Link to="/admin/notices" className="text-gray-600 hover:text-black whitespace-nowrap">Avisos</Link>
            <Link to="/admin/newsletter" className="text-gray-600 hover:text-black whitespace-nowrap">Newsletter</Link>
            <Link to="/admin/gallery" className="text-gray-600 hover:text-black whitespace-nowrap">Galeria</Link>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-3xl font-bold" style={{ fontFamily: 'Playfair Display' }}>Tarifas de Envio</h2>
            <p className="text-gray-600 mt-1">Gestiona los gastos de envio por zona geografica</p>
          </div>
          <Button onClick={seedDefaults} variant="outline" className="border-2 border-black">
            <RefreshCw className="w-4 h-4 mr-2" /> Restaurar valores por defecto
          </Button>
        </div>

        <div className="grid gap-6">
          {sortedRates.map((rate) => {
            const zoneInfo = ZONE_ICONS[rate.zone] || { emoji: '??', color: 'border-gray-400' };
            return (
              <div key={rate.zone} className={`bg-white border-2 ${zoneInfo.color} ${!rate.is_active ? 'opacity-50' : ''}`} data-testid={`zone-${rate.zone}`}>
                <div className="flex items-center justify-between p-4 border-b bg-gray-50">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded flex items-center justify-center font-bold text-sm border-2 ${zoneInfo.color}`}>
                      {zoneInfo.emoji}
                    </div>
                    <div>
                      <h3 className="font-bold text-lg">{rate.zone_label}</h3>
                      <p className="text-xs text-gray-500">{rate.tiers.length} tramos configurados</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => toggleActive(rate.zone)}
                      className={`text-xs font-medium px-3 py-1 rounded ${rate.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}
                    >
                      {rate.is_active ? 'Activo' : 'Inactivo'}
                    </button>
                    <Button
                      data-testid={`save-${rate.zone}`}
                      onClick={() => saveZone(rate.zone)}
                      className="bg-black text-white"
                      size="sm"
                      disabled={saving[rate.zone]}
                    >
                      <Save className="w-4 h-4 mr-1" /> {saving[rate.zone] ? 'Guardando...' : 'Guardar'}
                    </Button>
                  </div>
                </div>

                <div className="p-4">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 px-2">Pedido minimo (EUR)</th>
                        <th className="text-left py-2 px-2">Coste envio (EUR)</th>
                        <th className="text-left py-2 px-2">Etiqueta</th>
                        <th className="w-10"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {rate.tiers.map((tier, idx) => (
                        <tr key={`tier-${tier.min_subtotal ?? 'x'}-${idx}`} className="border-b hover:bg-gray-50">
                          <td className="py-2 px-2">
                            <Input
                              type="number" step="0.01" min="0"
                              className="w-32 text-sm"
                              value={tier.min_subtotal}
                              onChange={(e) => updateTier(rate.zone, idx, 'min_subtotal', e.target.value)}
                            />
                          </td>
                          <td className="py-2 px-2">
                            <Input
                              type="number" step="0.01" min="0"
                              className="w-32 text-sm"
                              value={tier.shipping_cost}
                              onChange={(e) => updateTier(rate.zone, idx, 'shipping_cost', e.target.value)}
                            />
                          </td>
                          <td className="py-2 px-2">
                            <Input
                              className="text-sm"
                              value={tier.label || ''}
                              onChange={(e) => updateTier(rate.zone, idx, 'label', e.target.value)}
                              placeholder="Descripcion del tramo"
                            />
                          </td>
                          <td className="py-2 px-2">
                            <button onClick={() => removeTier(rate.zone, idx)} className="text-red-500 hover:text-red-700">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <Button type="button" size="sm" variant="outline" className="mt-3 border-dashed" onClick={() => addTier(rate.zone)}>
                    <Plus className="w-4 h-4 mr-1" /> Añadir tramo
                  </Button>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-6 bg-white border-2 border-gray-200 p-4">
          <h4 className="font-bold mb-2">Como funciona</h4>
          <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
            <li>Cada zona tiene tramos basados en el subtotal del pedido</li>
            <li>El sistema aplica el primer tramo cuyo "Pedido minimo" sea menor o igual al subtotal</li>
            <li>Los tramos se evaluan de mayor a menor subtotal</li>
            <li>Ejemplo: Si el pedido es 55EUR, se aplicara el tramo con minimo 50EUR</li>
            <li>Pon coste 0 para envio gratuito en un tramo</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
